import React, { useState, useEffect, useContext } from 'react';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit2, FiLock, FiUserX, FiUserCheck, FiX } from 'react-icons/fi';
import api from '../services/api';
import { AuthContext } from '../context/AuthContext';

const UserManagement = () => {
  const { user: currentUser } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Add user form
  const [addForm, setAddForm] = useState({ username: '', full_name: '', password: '', role: 'user' });
  const [addErrors, setAddErrors] = useState({});
  const [addSubmitting, setAddSubmitting] = useState(false);

  // Edit user form
  const [editForm, setEditForm] = useState({ full_name: '', role: '', status: '' });
  const [editSubmitting, setEditSubmitting] = useState(false);

  // Reset password form
  const [newPassword, setNewPassword] = useState('');
  const [resetSubmitting, setResetSubmitting] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const res = await api.get('/auth/users');
      setUsers(res.data || []);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  // Validation
  const validateAddForm = () => {
    const errors = {};
    const usernameRegex = /^[a-zA-Z0-9_]{3,30}$/;
    if (!usernameRegex.test(addForm.username)) {
      errors.username = 'Username must be 3-30 characters, alphanumeric and underscore only';
    }
    if (!addForm.full_name.trim()) {
      errors.full_name = 'Full name is required';
    }
    if (addForm.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }
    setAddErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Add user
  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!validateAddForm()) return;
    try {
      setAddSubmitting(true);
      await api.post('/auth/register', {
        username: addForm.username,
        password: addForm.password,
        full_name: addForm.full_name,
        role: addForm.role,
      });
      toast.success('User created successfully');
      setShowAddModal(false);
      setAddForm({ username: '', full_name: '', password: '', role: 'user' });
      setAddErrors({});
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create user');
    } finally {
      setAddSubmitting(false);
    }
  };

  // Edit user
  const openEditModal = (u) => {
    setSelectedUser(u);
    setEditForm({ full_name: u.full_name, role: u.role, status: u.status });
    setShowEditModal(true);
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    try {
      setEditSubmitting(true);
      await api.put(`/auth/users/${selectedUser.id}`, {
        full_name: editForm.full_name,
        role: editForm.role,
        status: editForm.status,
      });
      toast.success('User updated successfully');
      setShowEditModal(false);
      setSelectedUser(null);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update user');
    } finally {
      setEditSubmitting(false);
    }
  };

  // Reset password
  const openResetModal = (u) => {
    setSelectedUser(u);
    setNewPassword('');
    setShowResetModal(true);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    try {
      setResetSubmitting(true);
      await api.put(`/auth/users/${selectedUser.id}/reset-password`, { new_password: newPassword });
      toast.success('Password reset successfully');
      setShowResetModal(false);
      setSelectedUser(null);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to reset password');
    } finally {
      setResetSubmitting(false);
    }
  };

  // Toggle active/inactive
  const handleToggleStatus = async (u) => {
    const newStatus = u.status === 'active' ? 'inactive' : 'active';
    try {
      if (newStatus === 'inactive') {
        await api.delete(`/auth/users/${u.id}`);
      } else {
        await api.put(`/auth/users/${u.id}`, { full_name: u.full_name, role: u.role, status: 'active' });
      }
      toast.success(`User ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
      loadUsers();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  };

  // Access control
  if (currentUser?.role !== 'admin') {
    return (
      <div className="card" style={{ padding: '40px', textAlign: 'center' }}>
        <h3>Access Denied</h3>
        <p>You do not have permission to view this page.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">USER MANAGEMENT</h2>
          <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
            <FiPlus /> Add User
          </button>
        </div>

        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <div className="loading-spinner"></div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Username</th>
                  <th style={thStyle}>Full Name</th>
                  <th style={thStyle}>Role</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Created</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan="6" style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                      No users found
                    </td>
                  </tr>
                ) : (
                  users.map((u) => (
                    <tr key={u.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={tdStyle}>{u.username}</td>
                      <td style={tdStyle}>{u.full_name}</td>
                      <td style={tdStyle}>
                        <span style={{
                          padding: '2px 10px',
                          borderRadius: '10px',
                          fontSize: '11px',
                          fontWeight: 600,
                          background: u.role === 'admin' ? '#dbeafe' : '#f3f4f6',
                          color: u.role === 'admin' ? '#1d4ed8' : '#6b7280',
                        }}>
                          {u.role}
                        </span>
                      </td>
                      <td style={tdStyle}>
                        <span style={{
                          padding: '2px 10px',
                          borderRadius: '10px',
                          fontSize: '11px',
                          fontWeight: 600,
                          background: u.status === 'active' ? '#dcfce7' : '#fee2e2',
                          color: u.status === 'active' ? '#059669' : '#dc2626',
                        }}>
                          {u.status}
                        </span>
                      </td>
                      <td style={tdStyle}>{formatDate(u.created_at)}</td>
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                          <button className="btn btn-sm btn-primary" onClick={() => openEditModal(u)} title="Edit">
                            <FiEdit2 /> Edit
                          </button>
                          <button
                            className="btn btn-sm"
                            style={{ background: '#f59e0b', color: '#fff', border: 'none', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px' }}
                            onClick={() => openResetModal(u)}
                            title="Reset Password"
                          >
                            <FiLock /> Reset
                          </button>
                          <button
                            className={`btn btn-sm ${u.status === 'active' ? 'btn-danger' : 'btn-primary'}`}
                            onClick={() => handleToggleStatus(u)}
                            title={u.status === 'active' ? 'Deactivate' : 'Activate'}
                          >
                            {u.status === 'active' ? <><FiUserX /> Deactivate</> : <><FiUserCheck /> Activate</>}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add User Modal */}
      {showAddModal && (
        <div style={overlayStyle} onClick={() => setShowAddModal(false)}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <div style={modalHeaderStyle}>
              <h3 style={{ margin: 0 }}>Add New User</h3>
              <button style={closeButtonStyle} onClick={() => setShowAddModal(false)}><FiX /></button>
            </div>
            <form onSubmit={handleAddUser}>
              <div className="form-group" style={{ marginBottom: '12px' }}>
                <label className="form-label">Username</label>
                <input
                  className="form-input"
                  type="text"
                  value={addForm.username}
                  onChange={(e) => setAddForm({ ...addForm, username: e.target.value })}
                  placeholder="3-30 chars, alphanumeric + underscore"
                />
                {addErrors.username && <span style={errorStyle}>{addErrors.username}</span>}
              </div>
              <div className="form-group" style={{ marginBottom: '12px' }}>
                <label className="form-label">Full Name</label>
                <input
                  className="form-input"
                  type="text"
                  value={addForm.full_name}
                  onChange={(e) => setAddForm({ ...addForm, full_name: e.target.value })}
                  placeholder="Enter full name"
                />
                {addErrors.full_name && <span style={errorStyle}>{addErrors.full_name}</span>}
              </div>
              <div className="form-group" style={{ marginBottom: '12px' }}>
                <label className="form-label">Password</label>
                <input
                  className="form-input"
                  type="password"
                  value={addForm.password}
                  onChange={(e) => setAddForm({ ...addForm, password: e.target.value })}
                  placeholder="Minimum 8 characters"
                />
                {addErrors.password && <span style={errorStyle}>{addErrors.password}</span>}
              </div>
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label className="form-label">Role</label>
                <select
                  className="form-input"
                  value={addForm.role}
                  onChange={(e) => setAddForm({ ...addForm, role: e.target.value })}
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button type="button" className="btn" onClick={() => setShowAddModal(false)} style={{ background: '#e5e7eb', color: '#374151', border: 'none', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer' }}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={addSubmitting}>
                  {addSubmitting ? 'Creating...' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div style={overlayStyle} onClick={() => setShowEditModal(false)}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <div style={modalHeaderStyle}>
              <h3 style={{ margin: 0 }}>Edit User: {selectedUser.username}</h3>
              <button style={closeButtonStyle} onClick={() => setShowEditModal(false)}><FiX /></button>
            </div>
            <form onSubmit={handleEditUser}>
              <div className="form-group" style={{ marginBottom: '12px' }}>
                <label className="form-label">Full Name</label>
                <input
                  className="form-input"
                  type="text"
                  value={editForm.full_name}
                  onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
                />
              </div>
              <div className="form-group" style={{ marginBottom: '12px' }}>
                <label className="form-label">Role</label>
                <select
                  className="form-input"
                  value={editForm.role}
                  onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label className="form-label">Status</label>
                <select
                  className="form-input"
                  value={editForm.status}
                  onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button type="button" className="btn" onClick={() => setShowEditModal(false)} style={{ background: '#e5e7eb', color: '#374151', border: 'none', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer' }}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={editSubmitting}>
                  {editSubmitting ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetModal && selectedUser && (
        <div style={overlayStyle} onClick={() => setShowResetModal(false)}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <div style={modalHeaderStyle}>
              <h3 style={{ margin: 0 }}>Reset Password: {selectedUser.username}</h3>
              <button style={closeButtonStyle} onClick={() => setShowResetModal(false)}><FiX /></button>
            </div>
            <form onSubmit={handleResetPassword}>
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label className="form-label">New Password</label>
                <input
                  className="form-input"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Minimum 8 characters"
                />
              </div>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button type="button" className="btn" onClick={() => setShowResetModal(false)} style={{ background: '#e5e7eb', color: '#374151', border: 'none', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer' }}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={resetSubmitting}>
                  {resetSubmitting ? 'Resetting...' : 'Reset Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Inline styles for table and modals
const thStyle = {
  padding: '10px 12px',
  textAlign: 'left',
  fontWeight: 600,
  fontSize: '12px',
  textTransform: 'uppercase',
  color: '#6b7280',
  borderBottom: '2px solid #e5e7eb',
  whiteSpace: 'nowrap',
};

const tdStyle = {
  padding: '10px 12px',
  fontSize: '13px',
  color: '#374151',
};

const overlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

const modalStyle = {
  background: '#fff',
  borderRadius: '8px',
  padding: '24px',
  width: '100%',
  maxWidth: '460px',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
};

const modalHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '20px',
};

const closeButtonStyle = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  fontSize: '18px',
  color: '#6b7280',
  padding: '4px',
};

const errorStyle = {
  color: '#dc2626',
  fontSize: '12px',
  marginTop: '4px',
  display: 'block',
};

export default UserManagement;
